import { Alert, Divider, Stack, Typography } from "@mui/material";
import { Children, ReactElement, cloneElement, isValidElement, useEffect, useState } from "react";
import { FieldValues, Path, useForm } from "react-hook-form";
import { Credential, ValidationErrors } from "../../graphql/types";
import { KEY_NAME_VALIDATION_ERRORS, KEY_NAME_VALIDATION_ERRORS_BY_ID } from "./CredentialValidationErrors";
import { FormController, IFormController } from "./FormController";
import { Button, FormSubmitButton } from "./KeyEditorCommon";
import { EditorButtonType, KeyFormValues } from "./KeyEditorTypes";

interface IFormWrapperProps<T extends FieldValues> {
  credential?: Credential;
  mode?: string;
  onFormSubmit?: (values: KeyFormValues) => void;
  loading?: boolean;
  buttonType?: EditorButtonType;
  buttonText?: string;
  isOauth?: boolean;
  validationErrors?: ValidationErrors;
  onLeave?: () => void;
  children?:
    ReactElement<IFormController<T | { key_name: string }>>[] | ReactElement<IFormController<T | { key_name: string }>>;
  alertMessage?: string;
}

const parseKeynameValidationErrors = (validationErrors?: ValidationErrors): string => {
  if (!validationErrors?.errors) return "";
  for (const error of validationErrors.errors) {
    if (!error) continue;
    if (error.typeId in KEY_NAME_VALIDATION_ERRORS) {
      return KEY_NAME_VALIDATION_ERRORS_BY_ID[error.typeId];
    }
  }
  return "";
};

const defaultButtonText = "Save Key";
const FormWrapper = <T extends FieldValues>(props: IFormWrapperProps<T>) => {
  const {
    credential,
    mode,
    onFormSubmit,
    loading,
    buttonType = "default",
    buttonText = defaultButtonText,
    children,
    isOauth,
    onLeave,
    validationErrors,
    alertMessage,
  } = props;
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    setError,
    watch,
    clearErrors,
    formState: { isValid, errors, isSubmitting },
  } = useForm<T | { key_name: string }>({
    mode: "onChange",
    defaultValues: {
      key_name: credential?.name || "",
    },
  });

  const actualButtonText = mode === "edit" ? "Save changes" : buttonText;

  const onSubmitHandler = () => {
    const formValues = getValues();
    if (isOauth) {
      if (!onFormSubmit) return;
      onFormSubmit({ name: formValues.key_name });
    } else {
      const properties: Record<string, unknown> = {};
      const cred: KeyFormValues = { properties };
      for (const [key, value] of Object.entries(formValues)) {
        if (key === "service_account_key") {
          properties["key_type"] = "json";
        }
        const trimValue = typeof value === "string" ? value.trim() : value;
        if (key === "key_name") {
          cred.name = trimValue;
        } else {
          properties[key] = trimValue;
        }
      }
      if (onFormSubmit) {
        onFormSubmit(cred);
      }
    }
  };
  useEffect(() => {
    if (credential) {
      const properties = credential?.properties as T;
      for (const [key, value] of Object.entries(properties)) {
        setValue(key as Path<T>, value);
      }
      if (credential.name) {
        setValue("key_name", credential.name);
      }
    }
  }, [credential, setValue]);
  const [editingField, setEditingField] = useState<string>("");

  useEffect(() => {
    const keyErrorMessage = parseKeynameValidationErrors(validationErrors);

    if (keyErrorMessage) {
      clearErrors();
      setError("key_name", {
        type: "manual",
        message: keyErrorMessage,
      });
    }
  }, [setError, validationErrors, clearErrors]);

  return (
    <>
      {alertMessage && (
        <Alert severity="warning" sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      )}
      <form autoComplete="off" onSubmit={handleSubmit(onSubmitHandler)}>
        <Stack sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 48px" }}>
          <FormController<T | { key_name: string }>
            elementType="textField"
            type="text"
            fieldName="key_name"
            label="Key Name"
            rules={{ required: true }}
            control={control}
            errors={errors}
            mode={mode}
            setValue={setValue}
            credential={credential}
            editingField={editingField}
            setEditingField={setEditingField}
            loading={loading}
          />

          <Divider sx={{ gridColumn: "span 2", margin: "12px 0" }} />
          {Children.map(children, (el) => {
            if (isValidElement<IFormController<T | { key_name: string }>>(el)) {
              return cloneElement(el, {
                control: control,
                errors: errors,
                mode: mode,
                setValue: setValue,
                credential: credential,
                watch: watch,
                getValues: getValues,
                editingField: editingField,
                setEditingField: setEditingField,
                loading: loading,
              });
            }
          })}
        </Stack>
        {mode === "edit" || mode === "add" ? (
          <Stack direction="row" sx={{ justifyContent: "space-between", marginTop: "40px", alignItems: "center" }}>
            <Button onClick={onLeave} variant="text" color="secondary">
              <Typography variant="button.large">Cancel</Typography>
            </Button>
            <FormSubmitButton
              isSubmitting={isSubmitting}
              loading={loading || false}
              isValid={isValid}
              buttonType={buttonType}
              buttonText={actualButtonText}
              mode={mode}
            />
          </Stack>
        ) : null}
      </form>
    </>
  );
};

export { FormWrapper, IFormWrapperProps };
